import { useEffect, useState, useCallback } from "react";
import { api, apiError, fmtDateLong } from "@/lib/api";
import { Flag, ShieldCheck, ScrollText } from "lucide-react";

export function AdminReports() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("open");
  const [error, setError] = useState("");
  const [note, setNote] = useState({});

  const load = useCallback(async () => {
    setRows((await api.get("/admin/reports", { params: status ? { status } : {} })).data);
  }, [status]);
  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn) => {
    setError("");
    try {
      await fn();
      await load();
    } catch (e) {
      setError(apiError(e));
    }
  };

  return (
    <div data-testid="admin-reports">
      <p className="overline">Admin</p>
      <h1 className="mt-3 flex items-center gap-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
        <Flag className="h-6 w-6 text-accentx" /> Reports
      </h1>
      <div className="mt-6 flex gap-2">
        {["open", "resolved", "dismissed", ""].map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatus(s)}
            data-testid={`reports-tab-${s || "all"}`}
            className={`rounded-full px-4 py-2 text-sm capitalize transition-colors ${
              status === s ? "bg-brand text-white" : "border border-slate-200 bg-white text-slate-600"
            }`}
          >
            {s || "all"}
          </button>
        ))}
      </div>
      {error && <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {rows.length === 0 ? (
        <p className="card-surface mt-6 p-8 text-sm text-slate-600" data-testid="reports-empty">
          No reports in this view.
        </p>
      ) : (
        <div className="mt-6 space-y-4">
          {rows.map((r) => (
            <div key={r.report_id} className="card-surface p-5" data-testid={`report-${r.report_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <span className="pill border-accentx/30 bg-accentx-soft text-accentx-hover">{r.category}</span>
                  <p className="mt-2 font-display text-lg font-semibold capitalize">
                    {r.target_type}: {r.target?.label || r.target_id}
                  </p>
                  <p className="mt-1 text-sm text-slate-500">
                    Reported by {r.reporter_name || "user"} · {fmtDateLong(r.created_at?.slice(0, 10))}
                  </p>
                  {r.description && <p className="mt-3 text-sm text-slate-600">"{r.description}"</p>}
                  {r.target?.notes && (
                    <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                      Reported content: {r.target.notes}
                    </p>
                  )}
                </div>
                <span className="pill border-slate-200 bg-slate-50 capitalize text-slate-600">{r.status}</span>
              </div>

              {r.status === "open" && (
                <div className="mt-4 space-y-3">
                  <input
                    className="field-input"
                    placeholder="Resolution note (optional)"
                    value={note[r.report_id] || ""}
                    onChange={(e) => setNote({ ...note, [r.report_id]: e.target.value })}
                    data-testid={`report-note-${r.report_id}`}
                  />
                  <div className="flex flex-wrap gap-3">
                    {r.target_type === "shift" && (
                      <button
                        className="btn-ghost border-red-200 text-red-700"
                        onClick={() =>
                          act(() =>
                            api.post(`/admin/shifts/${r.target_id}/remove`, { action: "resolved", note: note[r.report_id] })
                          )
                        }
                        data-testid={`report-remove-content-${r.report_id}`}
                      >
                        Remove shift content
                      </button>
                    )}
                    {r.target_type !== "shift" && (
                      <button
                        className="btn-ghost border-red-200 text-red-700"
                        onClick={() => act(() => api.post(`/admin/users/${r.target_id}/suspend`, { status: "suspend" }))}
                        data-testid={`report-suspend-${r.report_id}`}
                      >
                        Suspend account
                      </button>
                    )}
                    <button
                      className="btn-primary"
                      onClick={() =>
                        act(() => api.post(`/admin/reports/${r.report_id}/resolve`, { action: "resolved", note: note[r.report_id] }))
                      }
                      data-testid={`report-resolve-${r.report_id}`}
                    >
                      Mark resolved
                    </button>
                    <button
                      className="btn-ghost"
                      onClick={() =>
                        act(() => api.post(`/admin/reports/${r.report_id}/resolve`, { action: "dismissed", note: note[r.report_id] }))
                      }
                      data-testid={`report-dismiss-${r.report_id}`}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}
              {r.resolution && <p className="mt-3 text-sm text-slate-500">Note: {r.resolution}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminLegal() {
  const [versions, setVersions] = useState(null);
  const [consents, setConsents] = useState([]);
  const [audit, setAudit] = useState([]);

  const loadLegal = useCallback(async () => {
    const [v, c, a] = await Promise.all([
      api.get("/admin/legal/versions"),
      api.get("/admin/consents"),
      api.get("/admin/audit"),
    ]);
    setVersions(v.data);
    setConsents(c.data);
    setAudit(a.data);
  }, []);

  useEffect(() => {
    loadLegal();
  }, [loadLegal]);

  return (
    <div data-testid="admin-legal">
      <p className="overline">Admin</p>
      <h1 className="mt-3 flex items-center gap-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
        <ScrollText className="h-6 w-6 text-brand" /> Legal & compliance
      </h1>

      <div className="mt-8 grid gap-5 sm:grid-cols-3">
        {versions &&
          Object.entries(versions.documents).map(([key, d]) => (
            <div key={key} className="card-surface p-5" data-testid={`legal-version-${key}`}>
              <p className="overline capitalize">{key.replace("_", " ")}</p>
              <p className="mt-2 font-display text-2xl font-semibold">v{d.current}</p>
              <p className="mt-1 text-sm text-slate-500">{d.accepted_count} acceptances</p>
            </div>
          ))}
      </div>
      {versions && (
        <p className="mt-4 text-sm text-slate-500" data-testid="legal-dates-admin">
          Effective {versions.effective_date} · Last updated {versions.last_updated} ·{" "}
          {versions.total_consents} total consent records
        </p>
      )}

      <h2 className="mt-10 flex items-center gap-2 font-display text-xl font-semibold">
        <ShieldCheck className="h-4 w-4 text-slate-500" /> Consent records
      </h2>
      <div className="card-surface mt-4 overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="border-b border-slate-200 bg-slate-50/60 text-left">
            <tr className="[&>th]:px-5 [&>th]:py-3">
              <th className="overline">User</th>
              <th className="overline">Role</th>
              <th className="overline">Terms</th>
              <th className="overline">Privacy</th>
              <th className="overline">Acceptable use</th>
              <th className="overline">Accepted</th>
            </tr>
          </thead>
          <tbody>
            {consents.map((c) => (
              <tr key={c.consent_id} className="border-b border-slate-100 last:border-0" data-testid={`admin-consent-${c.consent_id}`}>
                <td className="px-5 py-3">
                  <p className="font-medium">{c.user_name}</p>
                  <p className="text-xs text-slate-400">{c.user_email}</p>
                </td>
                <td className="px-5 py-3">{c.user_role}</td>
                <td className="px-5 py-3">v{c.terms_version}</td>
                <td className="px-5 py-3">v{c.privacy_version}</td>
                <td className="px-5 py-3">v{c.acceptable_use_version}</td>
                <td className="px-5 py-3 text-slate-500">{c.accepted_at?.slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="mt-10 font-display text-xl font-semibold">Administrative audit trail</h2>
      <div className="card-surface mt-4 overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="border-b border-slate-200 bg-slate-50/60 text-left">
            <tr className="[&>th]:px-5 [&>th]:py-3">
              <th className="overline">When</th>
              <th className="overline">Admin</th>
              <th className="overline">Action</th>
              <th className="overline">Target</th>
              <th className="overline">Details</th>
            </tr>
          </thead>
          <tbody>
            {audit.map((a) => (
              <tr key={a.audit_id} className="border-b border-slate-100 last:border-0" data-testid={`audit-${a.audit_id}`}>
                <td className="px-5 py-3 text-slate-500">{a.created_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="px-5 py-3">{a.admin_name}</td>
                <td className="px-5 py-3">{a.action}</td>
                <td className="px-5 py-3 text-slate-600">
                  {a.target_type}: {a.target_id}
                </td>
                <td className="px-5 py-3 text-slate-500">{a.details || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
